import { Injectable } from '@angular/core';
import { AsyncSubject, BehaviorSubject, filter, Subject } from 'rxjs';
import { AccountHistory, AccountHistoryPagingResponse, AdvancedFilter, PagingInfo, PendingHistory } from './account-history';
import { MEAPIService } from 'src/app/common/services/api/me-api.service';
import { InternalSettingsService } from 'src/app/common/services/internal-settings/internal-settings.service';
import { MEAPIResponse, MEEntityAPIResponseHandler, ResponseCode } from 'src/app/common/services/api/me-api-response';
import { MEErrorService } from 'src/app/common/error/me-error.service';
import { MEError } from 'src/app/common/error/me-error';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import { Account } from 'src/app/common/services/accounts/accounts.interface';
import { MatDialog, MatDialogConfig, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { DialogConfig } from '@angular/cdk/dialog';
import { CustomDialogWidthConfig, ResponsiveDesignService } from 'src/app/common/services/responsive-design/responsive-design.service';
import { SearchFilterDialogComponent } from './search-filter-dialog/search-filter-dialog.component';
import { StringUtilityService } from 'src/app/system/utility/string-utility.service';

@Injectable({
  providedIn: 'root'
})
export class AccountHistoryService {

  public static readonly CRED_CARD = 'Credit Card Account';
  public static readonly FISERV = 'FISERV';

  readonly historyAPI : string = MEAPIService.BASE_URL + '/customer/account_history'

  public readonly DEFAULT_DAYS: number = 30;
  private readonly DEFAULT_PAGE_SIZE: number = 20;
  private readonly DEFAULT_PAGE_NUMBER: number = 0;

  private historyPagingList$: Subject<AccountHistoryPagingResponse> = new Subject<AccountHistoryPagingResponse>();
  public historyList$: BehaviorSubject<AccountHistory[]> = new BehaviorSubject<AccountHistory[]>([]);
  public pendingList$: BehaviorSubject<PendingHistory[]> = new BehaviorSubject<PendingHistory[]>([]);
  public pagingInfo$: BehaviorSubject<PagingInfo | null> = new BehaviorSubject<PagingInfo | null>({maxElements: 0, maxPageNumber: 0, currentPageNumber: 0});
  public currentDay$: BehaviorSubject<number> = new BehaviorSubject<number>(this.DEFAULT_DAYS);
  public currentPageSize$: BehaviorSubject<number> = new BehaviorSubject<number>(this.DEFAULT_PAGE_SIZE);
  public currentPageNumber$: BehaviorSubject<number> = new BehaviorSubject<number>(this.DEFAULT_PAGE_NUMBER);
  public advancedFilter$ : BehaviorSubject<AdvancedFilter | null> = new BehaviorSubject<AdvancedFilter | null>(null);

  public currentAccount$: BehaviorSubject<Account | null> = new BehaviorSubject<Account | null>(null);

  public actionNotSupported: boolean = false;

  private dialogRef!: MatDialogRef<SearchFilterDialogComponent, null>;

  private lastUrl: string = '';
  private currentUrl: string = '';

  constructor(private srvIs: InternalSettingsService,
                private srvApi: MEAPIService,
                private srvErr: MEErrorService,
                private srvTranslate: TranslateService,
                private srvRouter: Router,
                private srvDialog: MatDialog,
                private srvRpDesign : ResponsiveDesignService) {
      
      this.srvIs.internalSettings$.subscribe((is) => {
        if (!is) {
          this.historyPagingList$ = new Subject<AccountHistoryPagingResponse>();
          this.historyList$.next([]);
          this.pendingList$.next([]);
          this.pagingInfo$.next(null);
          this.currentAccount$.next(null);
          this.advancedFilter$.next(null);
          
        }

        srvRouter.events
        .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
        .subscribe((e: NavigationEnd) => {
          this.lastUrl = this.currentUrl;
          this.currentUrl = e.urlAfterRedirects;
        });
      })                  
    }

    private readonly setDialogConfig = (data: DialogConfig) => {
      const dialogConfig = new MatDialogConfig();
      dialogConfig.data = data;
      dialogConfig.maxWidth = 800;
      dialogConfig.panelClass = ['custom-dialog-download-history', new CustomDialogWidthConfig(this.srvRpDesign).getDialogWidthClass()];
      return dialogConfig;
    };
        
  public get currentAccount() : Account | null {
    return this.currentAccount$.value;
  }

  public set currentAccount(account: Account | null) {
    this.historyPagingList$ = new Subject<AccountHistoryPagingResponse>();
    this.historyList$.next([]);
    this.pendingList$.next([]);
    this.pagingInfo$.next(null);      
    this.currentAccount$.next(account);
  }

  public getCurrentAccount() : AsyncSubject<Account> {
    let ret = new AsyncSubject<Account>();

    this.currentAccount$.subscribe((acct) => {
      if (acct) {
        ret.next(acct);
        ret.complete();
      }
    })
    return ret;
  }

  public loadHistory(days : number, page : number, rows: number = 20) : void {
    if (!this.currentAccount$.value) {
      return;
    }
    let api = this.historyAPI + '?days_offset=' + days + '&page_number=' + page + '&rows_per_page=' + rows;
    let currentFilter = this.advancedFilter$.value;

    let bean = {
      accountKeyStr: this.currentAccount$.value.accountKey.key,
      ...currentFilter?.fromDate ? {fromDate : currentFilter.fromDate} : null,
      ...currentFilter?.toDate ? {toDate : currentFilter.toDate} : null,
      ...currentFilter?.fromAmount ? {fromAmount : currentFilter.fromAmount} : null,
      ...currentFilter?.toAmount ? {toAmount : currentFilter.toAmount} : null,
      ...currentFilter?.fromCheque ? {fromCheque : currentFilter.fromCheque} : null,
      ...currentFilter?.toCheque ? {toCheque : currentFilter.toCheque} : null,
      ...currentFilter?.primarySort ? { primarySort : currentFilter.primarySort} : null,
      ...currentFilter?.secondarySort ? { secondarySort : currentFilter.secondarySort} : null,
      ...currentFilter?.tranType && currentFilter?.tranType != 'ALL' ? { byDebit : currentFilter.tranType == 'DEBIT'} : null
    }
    this.srvApi.Post(api, 
                      bean, 
                      [
                       new SuccessfulHistoryHandler(ResponseCode.SUCCESS, this.historyList$, this.pendingList$, this.pagingInfo$, rows),
                       new AccountInvalidHandler(ResponseCode.ACCOUNT_INVALID, this.srvErr, this.srvTranslate, this.srvRouter),
                       new NoHistoryHandler(ResponseCode.NO_HISTORY_FOUND, this.historyList$, this.pendingList$, this.pagingInfo$),
                       new ActionNotSupportedHandler(ResponseCode.ACTION_NOT_SUPPORTED, this.actionNotSupported)
                      ])
  }

  showFilterDialog() {
        let config: DialogConfig = {      
        }
        
        // Close the previous dialog if exist
        if ((this.dialogRef) && (this.dialogRef.getState() == MatDialogState.OPEN)) {
            this.dialogRef.close();
        }

        this.dialogRef = this.srvDialog.open(SearchFilterDialogComponent, this.setDialogConfig(config))

        return this.dialogRef.afterClosed();
  }

  isAcctHistoryScope(): boolean {
    return this.lastUrl?.includes('account-history');
  }

  resetPaging() {
    this.currentDay$.next(this.DEFAULT_DAYS);
    this.currentPageNumber$.next(this.DEFAULT_PAGE_NUMBER);
    this.currentPageSize$.next(this.DEFAULT_PAGE_SIZE);
  }
}

class SuccessfulHistoryHandler implements MEEntityAPIResponseHandler<AccountHistoryPagingResponse> {
  constructor(public code: ResponseCode, 
              private historyList : BehaviorSubject<AccountHistory[]>,
              private pendingList: BehaviorSubject<PendingHistory[]>,
              private pagingInfo: BehaviorSubject<PagingInfo | null>,
              private rows : number) {}

  handle(response: MEAPIResponse<AccountHistoryPagingResponse>): boolean | MEError {
    

    if (response.entity) {
      let historyResponse = response.entity.historyresponse;
      if (historyResponse && historyResponse.length>1) {
        for (let i = 0; i < historyResponse.length; i++) {
          const item = historyResponse[i];
          item.isIntradayFirstTxn = !historyResponse[i - 1] || item.dateTime !== historyResponse[i - 1].dateTime;
          historyResponse[i].descDisplay = StringUtilityService.truncateText(historyResponse[i].extendedDescription || historyResponse[i].description);
        }
      }
      this.historyList.next(historyResponse);
      if (response.entity.pagingresponse.currentPageNumber == 0) {
        this.pendingList.next(response.entity.pendingTransactions);
      }
      response.entity.pagingresponse.maxElements = (response.entity.pagingresponse.maxPageNumber + 1) * this.rows;
      this.pagingInfo.next(response.entity.pagingresponse);
    }

    return true;
  }
}

class AccountInvalidHandler implements MEEntityAPIResponseHandler<AccountHistoryPagingResponse> {
  constructor(public code: ResponseCode,
              private srvErr : MEErrorService,
              private srvTranslate: TranslateService,
              private srvRouter: Router) {}

  handle(response: MEAPIResponse<AccountHistoryPagingResponse>): boolean | MEError {
    this.srvErr.showDialog('INVALID_ACCOUNT', [this.srvTranslate.instant('YOU_HAVE_BEEN_REDIRECTED_TO_DASHBOARD_PAGE_DUE_TO_INVALID'),
                                                this.srvTranslate.instant('ACCOUNT_REQUEST')])
                                                .subscribe(() => {
                                                  this.srvRouter.navigateByUrl('/dashboard')
                                                })
    return true;
  }
}

class NoHistoryHandler implements MEEntityAPIResponseHandler<AccountHistoryPagingResponse> {
  constructor(public code: ResponseCode,
                private historyList : BehaviorSubject<AccountHistory[]>,
                private pendingList: BehaviorSubject<PendingHistory[]>,
                private pagingInfo: BehaviorSubject<PagingInfo | null>) {}

  handle(response: MEAPIResponse<AccountHistoryPagingResponse>): boolean | MEError {
    this.historyList.next([]);
    this.pendingList.next([]);
    this.pagingInfo.next({maxElements: 0, maxPageNumber: 0, currentPageNumber: 0}); 

    return true;

  }
}

class ActionNotSupportedHandler implements MEEntityAPIResponseHandler<AccountHistoryPagingResponse> {
  constructor(public code: ResponseCode,
                private actionNotSupported: boolean) {}

  handle(response: MEAPIResponse<AccountHistoryPagingResponse>): boolean | MEError {
    this.actionNotSupported = false;

    return true;
  }
}